/**
 * Çalışma ve dinlenme saatleri (MLC 2006 Kural 2.3, Standart A2.3; STCW Kural VIII/1, Kod
 * A-VIII/1). Saf fonksiyonlar: kayıtlar ve günlerin sınırları parametreyle gelir; saat dilimi
 * çağıranın işidir (gemi saati farkı değişebilir, gün 23 ya da 25 saat sürebilir).
 *
 * Kayıt modeli: çalışma dönemleri kaydedilir; kaydın tam olduğu aralıkta geri kalan her an
 * dinlenmedir.
 */

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Sınırlar. Bayrak devleti iki rejimden birini seçer (Std A2.3 §5); STCW yalnız dinlenme
 * rejimini tanır (A-VIII/1 §2).
 */
export const REST_LIMITS = {
  /** Herhangi 24 saatte en az dinlenme [MLC A2.3 §5(b)(i); STCW A-VIII/1 §2.1]. */
  minRest24h: 10,
  /** Herhangi 7 günde en az dinlenme [MLC A2.3 §5(b)(ii); STCW A-VIII/1 §2.2]. */
  minRest7d: 77,
  /** Herhangi 24 saatte en fazla çalışma [MLC A2.3 §5(a)(i)]. */
  maxWork24h: 14,
  /** Herhangi 7 günde en fazla çalışma [MLC A2.3 §5(a)(ii)]. */
  maxWork7d: 72,
  /** Dinlenme en fazla iki parçaya bölünür [MLC A2.3 §6; STCW A-VIII/1 §3]. */
  maxRestPeriods: 2,
  /** Parçalardan biri en az 6 saat [MLC A2.3 §6; STCW A-VIII/1 §3]. */
  minLongestRest: 6,
  /** Ardışık dinlenmeler arası en fazla 14 saat [MLC A2.3 §6; STCW A-VIII/1 §3]. */
  maxInterval: 14,
} as const;

export type RestRegime = 'rest' | 'work';

/**
 * Çalışma dönemi türleri (müşteri ekranı: Normal Watch, Port Operations, Anchor Watch,
 * Maneuvering…). Hepsi çalışmadır; tür yalnız raporlama içindir. Acil durum ve tatbikat da
 * çalışma sayılır, ihlal görünür kalır (ADR 0016 §6).
 */
export const WORK_ACTIVITIES = [
  'watch',
  'anchor_watch',
  'maneuvering',
  'port_operations',
  'cargo',
  'maintenance',
  'drill',
  'emergency',
  'other',
] as const;
export type WorkActivity = (typeof WORK_ACTIVITIES)[number];

export type RestRule =
  | 'MIN_REST_24H'
  | 'MIN_REST_7D'
  | 'MAX_WORK_24H'
  | 'MAX_WORK_7D'
  | 'REST_PERIODS_MAX_TWO'
  | 'REST_PERIOD_MIN_6H'
  | 'REST_INTERVAL_MAX_14H';

export interface WorkPeriod {
  /** ISO-8601 UTC. */
  start: string;
  end: string;
  /** Tür (`WORK_ACTIVITIES`); acil durum bulguyu istisna olarak işaretler. */
  activity?: WorkActivity;
}

/** Bu süreden kısa ara dinlenme değildir (dakika; ILO/IMO rehberi, ADR 0018 §1). */
export const DEFAULT_SHORT_BREAK_MINUTES = 60;

export type WorkPeriodError =
  | 'INVALID_DATE'
  | 'END_NOT_AFTER_START'
  | 'OVERLAPPING_PERIODS'
  | 'FUTURE_DATE';

export interface RestDay {
  /** Gemi yerel tarihi (`YYYY-MM-DD`). */
  date: string;
  /** Günün başı ve sonu, ISO-8601 UTC; gemi saati farkıyla hesaplanmış. */
  start: string;
  end: string;
  /** Takvim günü tarih çizgisinde iki kez yaşandıysa oluşumu (ADR 0065 §4). */
  occurrence?: 1 | 2;
}

export interface RestFinding {
  rule: RestRule;
  severity: 'violation' | 'warning';
  /** Bulgunun ölçüldüğü aralık (pencere, dinlenme parçası ya da çalışma aralığı). */
  from: string;
  to: string;
  /** Ölçülen değer: saat ya da parça sayısı. */
  value: number;
  limit: number;
  /**
   * Pencere acil durum dönemine değiyor: ihlal yine ihlaldir ve görünür; kaptanın gerekçeli
   * imzası ve telafi dinlenmesi gerekir [MLC A2.3 §14; ADR 0018 §3].
   */
  exception?: boolean;
}

export interface RestDayReport {
  date: string;
  /** İki kez yaşanan günün oluşumu (tarih çizgisi; ADR 0065 §4). */
  occurrence?: 1 | 2;
  /** Günün içindeki çalışma ve dinlenme saatleri. */
  workHours: number;
  restHours: number;
  /** Gün sonunda biten 24 saat ve 7 günlük pencerelerdeki dinlenme; kayıt eksikse `null`. */
  rest24h: number | null;
  rest7d: number | null;
  findings: RestFinding[];
  status: 'compliant' | 'warning' | 'violation' | 'incomplete';
}

export interface RestCheckInput {
  periods: readonly WorkPeriod[];
  /** Kaydın tam olduğu aralık (ör. göreve başlama ile şimdi). Dışı değerlendirilmez. */
  recordedFrom: string;
  recordedTo: string;
  days: readonly RestDay[];
  regime?: RestRegime;
  /**
   * Uyarı payı (saat): sınıra bu kadar yaklaşan değer uyarıdır. Mevzuat kuralı değil, arayüz
   * tercihidir (docs/sorular.md).
   */
  warningMarginHours?: number;
  /**
   * "Kısa ara" eşiği (dakika): iki çalışma arasındaki daha kısa ara dinlenme sayılmaz, çalışmaya
   * katılır [MLC A2.3 §1(b)]. Varsayılan 60; bayrak devletine göre şirket ayarı.
   */
  shortBreakMinutes?: number;
}

interface Span {
  start: number;
  end: number;
}

/** Çalışma dönemlerinin denetimi: geçerli tarih, bitiş başlangıçtan sonra, çakışma yok. */
export function validateWorkPeriods(
  periods: readonly WorkPeriod[],
  serverNow: string,
): WorkPeriodError[] {
  const errors = new Set<WorkPeriodError>();
  const now = Date.parse(serverNow);
  const spans: Span[] = [];
  for (const period of periods) {
    const start = Date.parse(period.start);
    const end = Date.parse(period.end);
    if (Number.isNaN(start) || Number.isNaN(end) || Number.isNaN(now)) {
      errors.add('INVALID_DATE');
      continue;
    }
    if (end <= start) errors.add('END_NOT_AFTER_START');
    // "Gelecek tarih" gemi sunucusunun saatine göre (CLAUDE.md "Zaman")
    if (end > now) errors.add('FUTURE_DATE');
    spans.push({ start, end });
  }
  spans.sort((a, b) => a.start - b.start);
  for (let i = 1; i < spans.length; i += 1) {
    const previous = spans[i - 1];
    const current = spans[i];
    if (previous && current && current.start < previous.end) errors.add('OVERLAPPING_PERIODS');
  }
  return [...errors];
}

/**
 * Sıralı, birleşik çalışma aralıkları. Bitişik dönemler ve aralarındaki kısa ara tek aralık olur:
 * kısa ara dinlenme değildir [MLC A2.3 §1(b)].
 */
function normalize(periods: readonly WorkPeriod[], shortBreakMs = 0): Span[] {
  const spans = periods
    .map((period) => ({ start: Date.parse(period.start), end: Date.parse(period.end) }))
    .filter((span) => span.end > span.start)
    .sort((a, b) => a.start - b.start);
  const merged: Span[] = [];
  for (const span of spans) {
    const last = merged.at(-1);
    if (last && span.start - last.end < shortBreakMs) last.end = Math.max(last.end, span.end);
    else merged.push({ ...span });
  }
  return merged;
}

function workIn(spans: readonly Span[], from: number, to: number): number {
  let total = 0;
  for (const span of spans) {
    const overlap = Math.min(span.end, to) - Math.max(span.start, from);
    if (overlap > 0) total += overlap;
  }
  return total;
}

/** Dinlenme parçaları: kaydın tam olduğu aralıkta çalışma olmayan aralıklar. */
function restPeriods(spans: readonly Span[], from: number, to: number): Span[] {
  const rests: Span[] = [];
  let cursor = from;
  for (const span of spans) {
    if (span.end <= from || span.start >= to) continue;
    if (span.start > cursor) rests.push({ start: cursor, end: span.start });
    cursor = Math.max(cursor, span.end);
  }
  if (cursor < to) rests.push({ start: cursor, end: to });
  return rests;
}

const hours = (ms: number) => Math.round((ms / HOUR_MS) * 100) / 100;
const iso = (ms: number) => new Date(ms).toISOString();

/**
 * Bitişi `[dayStart, dayEnd]` içinde olan bütün pencereler arasında en çok çalışılanı. Pencere
 * kaydırıldıkça çalışma süresi parça parça doğrusal değişir; en büyüğü pencerenin başı ya da sonu
 * bir dönem sınırına ya da günün sınırına denk geldiğinde olur. Bu yüzden yalnız o noktalar
 * denenir ("herhangi 24 saat"). Gün başında biten pencere önceki günde de sayılır: sınırdaki
 * ihlal iki günde de görünür, hiçbirinde kaybolmaz.
 */
function worstWindow(
  spans: readonly Span[],
  length: number,
  dayStart: number,
  dayEnd: number,
  recordedFrom: number,
  recordedTo: number,
): { from: number; to: number; work: number } | null {
  const candidates = new Set<number>([dayStart, dayEnd]);
  for (const span of spans) {
    for (const edge of [span.start, span.end, span.start + length, span.end + length]) {
      if (edge >= dayStart && edge <= dayEnd) candidates.add(edge);
    }
  }
  let worst: { from: number; to: number; work: number } | null = null;
  for (const end of candidates) {
    const start = end - length;
    if (start < recordedFrom || end > recordedTo) continue;
    const work = workIn(spans, start, end);
    if (!worst || work > worst.work) worst = { from: start, to: end, work };
  }
  return worst;
}

/**
 * Gün gün MLC/STCW denetimi. Her gün için:
 * - bitişi o güne düşen bütün 24 saat ve 7 günlük pencereler (kayan pencere);
 * - o güne değen dinlenme parçalarının sayısı ve en uzunu (parça tam uzunluğuyla sayılır;
 *   gece yarısını aşan uyku tek parçadır);
 * - o gün biten çalışma aralığının (iki dinlenme arası) uzunluğu.
 */
export function checkRestHours(input: RestCheckInput): RestDayReport[] {
  const regime = input.regime ?? 'rest';
  const margin = input.warningMarginHours ?? 1;
  const recordedFrom = Date.parse(input.recordedFrom);
  const recordedTo = Date.parse(input.recordedTo);
  const spans = normalize(
    input.periods,
    (input.shortBreakMinutes ?? DEFAULT_SHORT_BREAK_MINUTES) * 60_000,
  );
  const emergencies = input.periods
    .filter((period) => period.activity === 'emergency')
    .map((period) => ({ start: Date.parse(period.start), end: Date.parse(period.end) }));
  const touchesEmergency = (from: number, to: number) =>
    emergencies.some((span) => span.start < to && span.end > from);
  const rests = restPeriods(spans, recordedFrom, recordedTo);

  return input.days.map((day) => {
    const dayStart = Date.parse(day.start);
    const dayEnd = Date.parse(day.end);
    const findings: RestFinding[] = [];
    const complete = dayStart >= recordedFrom && dayEnd <= recordedTo;
    const from = Math.max(dayStart, recordedFrom);
    const to = Math.min(dayEnd, recordedTo);
    const work = to > from ? workIn(spans, from, to) : 0;
    const report = (length: number) => {
      const end = dayEnd;
      return end - length >= recordedFrom && end <= recordedTo
        ? hours(length - workIn(spans, end - length, end))
        : null;
    };

    /** Alt sınır (dinlenme) ya da üst sınır (çalışma) için bulgu. */
    const judge = (
      rule: RestRule,
      value: number,
      limit: number,
      kind: 'min' | 'max',
      window: { from: number; to: number },
    ) => {
      const violated = kind === 'min' ? value < limit : value > limit;
      const near = kind === 'min' ? value < limit + margin : value > limit - margin;
      if (violated || near) {
        findings.push({
          rule,
          severity: violated ? 'violation' : 'warning',
          from: iso(window.from),
          to: iso(window.to),
          value,
          limit,
          ...(touchesEmergency(window.from, window.to) ? { exception: true } : {}),
        });
      }
    };

    if (complete) {
      const day24 = worstWindow(spans, DAY_MS, dayStart, dayEnd, recordedFrom, recordedTo);
      const week = worstWindow(spans, WEEK_MS, dayStart, dayEnd, recordedFrom, recordedTo);
      if (regime === 'rest') {
        if (day24)
          judge('MIN_REST_24H', hours(DAY_MS - day24.work), REST_LIMITS.minRest24h, 'min', day24);
        if (week)
          judge('MIN_REST_7D', hours(WEEK_MS - week.work), REST_LIMITS.minRest7d, 'min', week);
      } else {
        if (day24) judge('MAX_WORK_24H', hours(day24.work), REST_LIMITS.maxWork24h, 'max', day24);
        if (week) judge('MAX_WORK_7D', hours(week.work), REST_LIMITS.maxWork7d, 'max', week);
      }

      // Dinlenmenin bölünmesi [MLC A2.3 §6; STCW A-VIII/1 §3]: her iki rejimde de uygulanır
      const touching = rests.filter((rest) => rest.end > dayStart && rest.start < dayEnd);
      if (touching.length > REST_LIMITS.maxRestPeriods) {
        const first = touching[0];
        const last = touching.at(-1);
        if (first && last) {
          findings.push({
            rule: 'REST_PERIODS_MAX_TWO',
            severity: 'violation',
            from: iso(first.start),
            to: iso(last.end),
            value: touching.length,
            limit: REST_LIMITS.maxRestPeriods,
            ...(touchesEmergency(dayStart, dayEnd) ? { exception: true } : {}),
          });
        }
      }
      const longest = touching.reduce<Span | null>(
        (best, rest) => (!best || rest.end - rest.start > best.end - best.start ? rest : best),
        null,
      );
      const longestHours = longest ? hours(longest.end - longest.start) : 0;
      if (longestHours < REST_LIMITS.minLongestRest) {
        findings.push({
          rule: 'REST_PERIOD_MIN_6H',
          severity: 'violation',
          from: iso(longest?.start ?? dayStart),
          to: iso(longest?.end ?? dayEnd),
          value: longestHours,
          limit: REST_LIMITS.minLongestRest,
          ...(touchesEmergency(dayStart, dayEnd) ? { exception: true } : {}),
        });
      }

      // İki dinlenme arası: o gün biten çalışma aralıkları
      for (let i = 1; i < rests.length; i += 1) {
        const previous = rests[i - 1];
        const next = rests[i];
        if (!previous || !next || next.start <= dayStart || next.start > dayEnd) continue;
        judge(
          'REST_INTERVAL_MAX_14H',
          hours(next.start - previous.end),
          REST_LIMITS.maxInterval,
          'max',
          { from: previous.end, to: next.start },
        );
      }
    }

    const status: RestDayReport['status'] = !complete
      ? 'incomplete'
      : findings.some((finding) => finding.severity === 'violation')
        ? 'violation'
        : findings.length > 0
          ? 'warning'
          : 'compliant';
    return {
      date: day.date,
      ...(day.occurrence ? { occurrence: day.occurrence } : {}),
      workHours: hours(work),
      restHours: hours(Math.max(0, to - from - work)),
      rest24h: report(DAY_MS),
      rest7d: report(WEEK_MS),
      findings,
      status,
    };
  });
}
